import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import type { Contact, ContactFilters, ContactCategoryRow, ContactTagRow } from '../types';

const PAGE_SIZE = 50;

// 'tenant_contact_categories' e 'tenant_contact_tags' podem ainda não estar nos
// tipos gerados até o próximo db:types — usando cast controlado.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const db = supabase as any;

export interface UseContactsResult {
  contacts: Contact[];
  total: number;
  isLoading: boolean;
  isError: boolean;
  refetch: () => void;
}

interface ContactsPage {
  contacts: Contact[];
  total: number;
}

export function useContacts(
  filters: ContactFilters,
  page = 0,
): UseContactsResult {
  const { data, isLoading, isError, refetch } = useQuery<ContactsPage>({
    queryKey: ['contacts', filters, page],
    queryFn: async (): Promise<ContactsPage> => {
      let query = supabase
        .from('contacts')
        .select(
          `id, tenant_id, full_name, phone, phone_whatsapp, email,
           neighborhood, city, state_code, category, tags,
           engagement_score, active, leader_id, created_at, updated_at,
           latitude, longitude,
           leader:leader_id(id, full_name)`,
          { count: 'exact' },
        )
        .order('full_name', { ascending: true })
        .range(page * PAGE_SIZE, (page + 1) * PAGE_SIZE - 1);

      if (filters.activeOnly) {
        query = query.eq('active', true);
      }

      if (filters.category !== 'all') {
        query = query.eq('category', filters.category);
      }

      if (filters.neighborhood) {
        query = query.ilike('neighborhood', `%${filters.neighborhood}%`);
      }

      if (filters.search.trim()) {
        query = query.or(
          `full_name.ilike.%${filters.search}%,phone.ilike.%${filters.search}%,email.ilike.%${filters.search}%`,
        );
      }

      const { data: rows, error, count } = await query;
      if (error) throw error;
      // Os campos latitude/longitude foram adicionados na migration 20260522000200_geo.sql.
      // O cast para unknown→Contact[] é necessário até o próximo `npm run db:types`.
      return { contacts: (rows ?? []) as unknown as Contact[], total: count ?? 0 };
    },
    staleTime: 30_000,
    placeholderData: (prev: ContactsPage | undefined) => prev,
  });

  return {
    contacts: data?.contacts ?? [],
    total: data?.total ?? 0,
    isLoading,
    isError,
    refetch,
  };
}

export function useContact(id: string | null) {
  return useQuery<Contact | null>({
    queryKey: ['contact', id],
    queryFn: async (): Promise<Contact | null> => {
      if (!id) return null;
      const { data, error } = await supabase
        .from('contacts')
        .select('*, leader:leader_id(id, full_name)')
        .eq('id', id)
        .single();
      if (error) throw error;
      return data as unknown as Contact;
    },
    enabled: !!id,
  });
}

export function useLeaders() {
  return useQuery<Pick<Contact, 'id' | 'full_name'>[]>({
    queryKey: ['contacts', 'leaders'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('contacts')
        .select('id, full_name')
        .eq('category', 'lideranca')
        .eq('active', true)
        .order('full_name');
      if (error) throw error;
      return (data ?? []) as Pick<Contact, 'id' | 'full_name'>[];
    },
    staleTime: 60_000,
  });
}

interface ContactStats {
  total: number;
  lideranca: number;
  voluntario: number;
  doador: number;
  eleitor: number;
  simpatizante: number;
  inactive: number;
}

export function useContactStats() {
  return useQuery<ContactStats>({
    queryKey: ['contacts', 'stats'],
    queryFn: async (): Promise<ContactStats> => {
      const { data, error } = await supabase
        .from('contacts')
        .select('category, active');
      if (error) throw error;

      const all = data ?? [];
      return {
        total:        all.filter((c) => c.active).length,
        lideranca:    all.filter((c) => c.active && c.category === 'lideranca').length,
        voluntario:   all.filter((c) => c.active && c.category === 'voluntario').length,
        doador:       all.filter((c) => c.active && c.category === 'doador').length,
        eleitor:      all.filter((c) => c.active && c.category === 'eleitor').length,
        simpatizante: all.filter((c) => c.active && c.category === 'simpatizante').length,
        inactive:     all.filter((c) => !c.active).length,
      };
    },
    staleTime: 60_000,
  });
}

// ─── Categorias configuráveis do tenant ──────────────────────────────────────
// RLS retorna: categorias do tenant ativo + padrões de plataforma (tenant_id IS NULL)
export function useContactCategories() {
  return useQuery<ContactCategoryRow[]>({
    queryKey: ['contact-categories'],
    queryFn: async (): Promise<ContactCategoryRow[]> => {
      const { data, error } = await db
        .from('tenant_contact_categories')
        .select('*')
        .eq('active', true)
        .order('sort_order');
      if (error) throw error;
      return (data ?? []) as ContactCategoryRow[];
    },
    staleTime: 5 * 60_000, // 5 min — muda raramente
  });
}

// ─── Tags configuráveis do tenant ────────────────────────────────────────────
export function useContactTags() {
  return useQuery<ContactTagRow[]>({
    queryKey: ['contact-tags'],
    queryFn: async (): Promise<ContactTagRow[]> => {
      const { data, error } = await db
        .from('tenant_contact_tags')
        .select('*')
        .eq('active', true)
        .order('sort_order', { ascending: true })
        .order('label', { ascending: true });
      if (error) throw error;
      return (data ?? []) as ContactTagRow[];
    },
    staleTime: 60_000,
  });
}
